import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight } from "lucide-react";

const ProjectsPage = () => {
  const projects = [
    {
      title: "Modern Backyard Oasis",
      location: "Beverly Hills, CA",
      category: "Landscape Design",
      description: "Complete backyard transformation featuring custom patio, native plants, and outdoor living space.",
      image: "https://images.unsplash.com/photo-1762608675427-09ac2dbd1540?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYWNreWFyZCUyMHBhdGlvJTIwZGVzaWduJTIwcGF2aW5nJTIwbGFuZHNjYXBpbmd8ZW58MHx8fHwxNzcyNzA2MjUzfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Estate Garden Maintenance",
      location: "Bel Air, CA",
      category: "Garden Maintenance",
      description: "Ongoing maintenance program for a 2-acre estate featuring formal gardens and lawns.",
      image: "https://images.unsplash.com/photo-1759355787118-7b48305965d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBtYW5pY3VyZWQlMjBob21lJTIwZ2FyZGVuJTIwbGF3biUyMGxhbmRzY2FwaW5nfGVufDB8fHx8MTc3MjcwNjI1Mnww&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Front Yard Lawn Installation",
      location: "Pasadena, CA",
      category: "Lawn Installation",
      description: "New lawn installation with drought-tolerant grass variety and efficient irrigation system.",
      image: "https://images.unsplash.com/photo-1689728318937-17d24bc0a65c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBnYXJkZW5lciUyMHdvcmtpbmclMjBpbiUyMGdhcmRlbiUyMGxhd24lMjBtb3dlcnxlbnwwfHx8fDE3NzI3MDYyNTN8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Coastal Garden Design",
      location: "Malibu, CA",
      category: "Landscape Design",
      description: "Salt-tolerant garden design featuring native coastal plants and ocean views.",
      image: "https://images.unsplash.com/photo-1728706613021-e447801e1ea6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBnYXJkZW5lciUyMHdvcmtpbmclMjBpbiUyMGdhcmRlbiUyMGxhd24lMjBtb3dlcnxlbnwwfHx8fDE3NzI3MDYyNTN8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Suburban Family Garden",
      location: "Santa Monica, CA",
      category: "Garden Planting",
      description: "Family-friendly backyard with play areas, vegetable garden, and native planting.",
      image: "https://images.unsplash.com/photo-1770169096244-90f4288392d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxiZWF1dGlmdWwlMjBtYW5pY3VyZWQlMjBob21lJTIwZ2FyZGVuJTIwbGF3biUyMGxhbmRzY2FwaW5nfGVufDB8fHx8MTc3MjcwNjI1Mnww&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Irrigation System Upgrade",
      location: "Glendale, CA",
      category: "Irrigation",
      description: "Smart irrigation system installation reducing water usage by 40%.",
      image: "https://images.unsplash.com/photo-1758524053982-dc0fc7cd651f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGhvbWVvd25lciUyMGNvdXBsZSUyMGluJTIwZ2FyZGVufGVufDB8fHx8MTc3MjcwNjI1NHww&ixlib=rb-4.1.0&q=85",
    },
  ];

  return (
    <div data-testid="projects-page" className="min-h-screen bg-[#FAFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-[#2D5A27] py-20 md:py-28">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1
                data-testid="projects-page-headline"
                className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6"
              >
                Our Projects
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Browse our portfolio of completed landscaping projects across Los Angeles. Each project reflects our commitment to quality and attention to detail.
              </p>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <div
                  key={index}
                  data-testid={`project-detail-${index}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm text-[#2D5A27] text-xs font-medium px-3 py-1.5 rounded-full">
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-[#1A2C22] mb-2">
                      {project.title}
                    </h3>
                    <div className="flex items-center gap-1 text-[#6B7280] text-sm mb-3">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </div>
                    <p className="text-[#4B5563] text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#1A2C22] py-16 md:py-20">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl md:text-4xl text-white font-bold mb-6">
              Let's Create Something Beautiful
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Ready to transform your outdoor space? Contact us for a free consultation.
            </p>
            <Link to="/contact">
              <Button
                data-testid="projects-cta"
                className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-14 px-10 text-lg rounded-full"
              >
                Start Your Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectsPage;
